<template>
    <!-- Decorativo: o nome e o status do canal já estão no texto do hero. -->
    <div class="hero-art" :style="artStyle" aria-hidden="true"
        @mousemove="onPointerMove" @mouseleave="onPointerLeave">

        <img v-if="heroImage" :src="heroImage" alt="" class="hero-art-image" />

        <div v-else class="scene">
            <div class="layer" style="--depth: .25">
                <span class="blob"></span>
            </div>

            <div class="layer" style="--depth: .55">
                <div class="float product-card">
                    <span class="product-photo">
                        <fa icon="tags" />
                    </span>
                    <span class="sk-line"></span>
                    <span class="sk-line sk-line-short"></span>
                    <span class="product-price"></span>
                </div>
            </div>

            <div class="layer" style="--depth: .8">
                <div class="float phone">
                    <span class="phone-notch"></span>
                    <div class="phone-screen">
                        <BrandTileCmp class="phone-tile" :size="58" :image="markUrl" :icon="isCustom ? 'store-alt' : null"
                            :text="brand.monogram" :background="brand.color" :color="brand.textColor" />
                        <span class="phone-name">{{realName}}</span>
                        <span class="phone-pill"></span>
                        <span class="phone-pill phone-pill-short"></span>
                    </div>
                </div>
            </div>

            <div class="layer" style="--depth: 1.05">
                <div class="float bag">
                    <span class="bag-handle"></span>
                    <span class="bag-body"></span>
                </div>
            </div>

            <div class="layer" style="--depth: 1.2">
                <div class="float chart-card">
                    <span class="chart-label">Vendas</span>
                    <span class="chart-bars">
                        <span v-for="(h, i) in bars" :key="i" class="chart-bar" :style="{ '--h': h, '--i': i }"></span>
                    </span>
                </div>
            </div>

            <div class="layer" style="--depth: 1.4">
                <div class="float order-pill">
                    <fa icon="check-circle" class="order-pill-icon" />
                    Novo pedido
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getChannelBrand, getChannelGlow } from '@/channel/models/ChannelBrand'
import { ChannelEnum } from '@c2b/muven-commons'
import BrandTileCmp from '@/utils/components/BrandTileCmp.vue'

/*
 * Ilustração de propósito para o canal: o PNG de src/channel/images/heroes/ com
 * o mesmo nome do símbolo (ex.: tiktok-shop.png) ou, para canal sem símbolo, o
 * nome do enum em minúsculas (ex.: netshoes.png). Sem arquivo, a cena é montada
 * aqui com a cor e o símbolo da marca, então todo canal tem a sua.
 */
const heroImages = require.context('@/channel/images/heroes', false, /\.(png|webp|jpe?g)$/)

export default {
    name: 'ChannelHeroArtCmp',
    components: {
        BrandTileCmp,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            pointerX: 0,
            pointerY: 0,
            bars: [.32, .5, .42, .72, 1],
        }
    },
    computed: {
        brand() {
            return getChannelBrand(this.channel.enumChannel)
        },
        isCustom() {
            return this.channel.enumChannel === ChannelEnum.Custom
        },
        realName() {
            return this.channel.getRealName ? this.channel.getRealName() : this.channel.name
        },
        markUrl() {
            return this.brand.mark ? require(`@/channel/images/marks/${this.brand.mark}`) : null
        },
        heroImage() {
            const name = this.brand.mark
                ? this.brand.mark.replace(/\.\w+$/, '')
                : String(ChannelEnum[this.channel.enumChannel] || '').toLowerCase()
            const key = heroImages.keys().find(k => k.replace(/^\.\//, '').replace(/\.\w+$/, '') === name)

            return key ? heroImages(key) : null
        },
        artStyle() {
            return {
                '--hero-brand': this.brand.color,
                '--hero-on-brand': this.brand.textColor,
                '--hero-glow': getChannelGlow(this.brand) || 'var(--brand)',
                '--mx': this.pointerX,
                '--my': this.pointerY,
            }
        },
    },
    methods: {
        onPointerMove(ev) {
            const rect = this.$el.getBoundingClientRect()

            this.pointerX = ((ev.clientX - rect.left) / rect.width - .5) * 2
            this.pointerY = ((ev.clientY - rect.top) / rect.height - .5) * 2
        },
        onPointerLeave() {
            this.pointerX = 0
            this.pointerY = 0
        },
    },
}
</script>

<style lang="scss" scoped>
$ease-out: cubic-bezier(.22, 1, .36, 1);

// Monocromática de propósito: a cor da marca fica só no celular, o resto é
// papel neutro. Sem movimento contínuo, só a entrada e o parallax do ponteiro.
.hero-art {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 200px;
}

.scene {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 420px;
    height: 230px;
    transform: translate(-50%, -50%) scale(.85);
}

.hero-art-image {
    position: absolute;
    right: 0;
    bottom: 0;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    animation: art-in .8s $ease-out both;
}

/* ── Camadas: cada uma anda conforme o ponteiro, na proporção da profundidade ── */

.layer {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translate3d(calc(var(--mx) * var(--depth) * 10px), calc(var(--my) * var(--depth) * 8px), 0);
    transition: transform .6s $ease-out;
    animation: art-in .9s $ease-out both;
    animation-delay: calc(var(--depth) * .18s);
}

.float {
    position: absolute;
}

/* ── Luz ambiente ── */

.blob {
    position: absolute;
    top: 10%;
    left: 34%;
    width: 200px;
    height: 190px;
    border-radius: 50%;
    background: color-mix(in srgb, var(--hero-glow) 22%, transparent);
    filter: blur(34px);
}

/* ── Cartão de produto ── */

.product-card {
    top: 22%;
    left: 4%;
    display: flex;
    flex-direction: column;
    gap: 7px;
    width: 112px;
    padding: 9px;
    background: var(--surface-overlay);
    border: 1px solid var(--border-subtle);
    border-radius: 16px;
    box-shadow: var(--shadow-lg);
    rotate: -9deg;
}

.product-photo {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 62px;
    border-radius: 11px;
    background: var(--surface-3);
    color: var(--text-faint);
    font-size: 22px;
}

.sk-line {
    height: 6px;
    border-radius: 3px;
    background: var(--surface-3);
}

.sk-line-short {
    width: 60%;
}

.product-price {
    width: 46%;
    height: 12px;
    border-radius: 6px;
    background: var(--border-strong);
}

/* ── Celular ── */

.phone {
    top: 2%;
    left: 36%;
    width: 124px;
    height: 226px;
    padding: 7px;
    background: var(--hero-device);
    border: 2px solid var(--hero-device-edge);
    border-radius: 28px;
    box-shadow: var(--shadow-lg), 0 24px 50px -14px color-mix(in srgb, var(--hero-glow) 35%, transparent);
    rotate: 11deg;
}

.phone-notch {
    position: absolute;
    top: 12px;
    left: 50%;
    z-index: 1;
    width: 34px;
    height: 9px;
    border-radius: 5px;
    background: var(--hero-device);
    transform: translateX(-50%);
}

.phone-screen {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 9px;
    height: 100%;
    overflow: hidden;
    border-radius: 21px;
    background:
        radial-gradient(circle at 80% 10%, color-mix(in srgb, var(--hero-glow) 65%, transparent), transparent 55%),
        linear-gradient(165deg, color-mix(in srgb, var(--hero-brand) 80%, var(--hero-glow)), var(--hero-brand));

    // Reflexo de vidro
    &::after {
        content: '';
        position: absolute;
        top: -30%;
        left: -60%;
        width: 70%;
        height: 160%;
        background: linear-gradient(90deg, transparent, color-mix(in srgb, var(--white) 18%, transparent), transparent);
        rotate: 20deg;
    }
}

.phone-tile {
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--white) 75%, transparent), 0 10px 24px color-mix(in srgb, var(--shadow-ink) 35%, transparent);
}

.phone-name {
    // Nomes longos (Mercado Livre, Tray by Fbits) quebram em duas linhas
    display: -webkit-box;
    max-width: 100%;
    padding: 0 8px;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    color: var(--hero-on-brand);
    font-family: 'Bricolage Grotesque', 'Source Sans Pro', sans-serif;
    font-size: 15px;
    line-height: 1.1;
    text-align: center;
    font-weight: 800;
    letter-spacing: -.3px;
    text-shadow: 0 1px 6px color-mix(in srgb, var(--shadow-ink) 55%, transparent);
}

.phone-pill {
    width: 64%;
    height: 7px;
    border-radius: 4px;
    background: color-mix(in srgb, var(--hero-on-brand) 40%, transparent);
}

.phone-pill-short {
    width: 40%;
    margin-top: -4px;
}

/* ── Sacola ── */

.bag {
    top: 58%;
    left: 22%;
    width: 58px;
    height: 70px;
    rotate: -14deg;
}

.bag-handle {
    position: absolute;
    top: 0;
    left: 50%;
    width: 30px;
    height: 26px;
    border: 5px solid var(--border-strong);
    border-bottom: none;
    border-radius: 15px 15px 0 0;
    transform: translateX(-50%);
}

.bag-body {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 52px;
    border-radius: 10px 10px 14px 14px;
    border: 1px solid var(--border);
    background: linear-gradient(150deg, var(--surface-1), var(--surface-3));
    box-shadow: var(--shadow-md);
}

/* ── Gráfico ── */

.chart-card {
    top: 40%;
    right: 0;
    width: 132px;
    padding: 10px 12px 12px;
    background: color-mix(in srgb, var(--surface-overlay) 88%, transparent);
    border: 1px solid var(--border-subtle);
    border-radius: 16px;
    box-shadow: var(--shadow-lg);
    backdrop-filter: blur(8px);
    rotate: 5deg;
}

.chart-label {
    display: block;
    margin-bottom: 8px;
    color: var(--text-strong);
    font-size: 11px;
    font-weight: 700;
}

.chart-bars {
    display: flex;
    align-items: flex-end;
    gap: 6px;
    height: 48px;
}

.chart-bar {
    flex: 1;
    height: calc(var(--h) * 100%);
    border-radius: 4px 4px 2px 2px;
    background: var(--border-strong);

    // Só a última barra (o "agora") ganha destaque
    &:last-child {
        background: var(--text-faint);
    }
    transform-origin: bottom;
    animation: bar-grow 1s $ease-out both;
    animation-delay: calc(.6s + var(--i) * .09s);
}

/* ── Pílula de pedido ── */

.order-pill {
    top: 6%;
    left: 8%;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px 6px 8px;
    background: var(--surface-overlay);
    border: 1px solid var(--border-subtle);
    border-radius: 999px;
    box-shadow: var(--shadow-md);
    color: var(--text-strong);
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
}

.order-pill-icon {
    color: var(--success-fg);
}

@keyframes art-in {
    from { opacity: 0; translate: 0 18px; }
    to   { opacity: 1; translate: 0 0; }
}

@keyframes bar-grow {
    from { transform: scaleY(0); }
    to   { transform: scaleY(1); }
}

@media (prefers-reduced-motion: reduce) {
    .layer,
    .chart-bar,
    .hero-art-image {
        animation: none;
        transition: none;
    }

    .layer {
        transform: none;
    }
}
</style>
